import type { LinkPreview, Message } from '@nexo/types';
import { AvatarStack, cn, useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pin } from 'lucide-react';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { cache } from '../../lib/cache';
import { formatRelative } from '../../lib/format';
import { isDesktop, openExternal } from '../../lib/platform';
import { keys } from '../../lib/queries';
import { useMe } from '../auth/AuthProvider';
import { EmojiPicker } from './EmojiPicker';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '../../lib/forms';

/** Reactions, pin and bookmark mutations for a message (optimistic where it matters). */
export function useMessageActions(message: Message) {
  const { team } = useTeam();
  const me = useMe();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  const onError = (err: unknown) =>
    toast({ title: t('No se pudo completar'), description: errorMessage(err), tone: 'error' });

  const react = useMutation({
    mutationFn: ({ emoji, on }: { emoji: string; on: boolean }) =>
      api.messages.react(message.id, emoji, on),
    onMutate: ({ emoji, on }) => {
      const reactions = message.reactions
        .map((r) =>
          r.emoji === emoji
            ? {
                ...r,
                count: r.count + (on ? 1 : -1),
                userIds: on ? [...r.userIds, me.id] : r.userIds.filter((id) => id !== me.id),
              }
            : r,
        )
        .filter((r) => r.count > 0);
      if (on && !message.reactions.some((r) => r.emoji === emoji)) {
        reactions.push({ emoji, count: 1, userIds: [me.id] });
      }
      cache.upsertMessage(queryClient, { ...message, reactions });
    },
    onSuccess: (updated) => cache.upsertMessage(queryClient, updated),
    onError: (err) => {
      cache.upsertMessage(queryClient, message);
      onError(err);
    },
  });

  const pin = useMutation({
    mutationFn: (pinned: boolean) => api.messages.pin(message.id, pinned),
    onSuccess: (updated) => cache.upsertMessage(queryClient, updated),
    onError,
  });

  const save = useMutation({
    mutationFn: (saved: boolean) => api.messages.save(message.id, saved),
    onMutate: (saved) =>
      queryClient.setQueryData<string[]>(keys.savedIds(team.id), (ids = []) =>
        saved ? [...ids, message.id] : ids.filter((id) => id !== message.id),
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.saved(team.id) });
    },
    onError,
  });

  const toggleReaction = (emoji: string) => {
    const mine = message.reactions.find((r) => r.emoji === emoji)?.userIds.includes(me.id);
    react.mutate({ emoji, on: !mine });
  };

  return { toggleReaction, pin, save };
}

export const QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '👀', '✅'];

export function MessageReactions({ message }: { message: Message }) {
  const me = useMe();
  const { memberById } = useTeam();
  const { toggleReaction } = useMessageActions(message);
  const { t } = useTranslation();
  if (message.reactions.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1">
      {message.reactions.map((reaction) => {
        const mine = reaction.userIds.includes(me.id);
        const names = reaction.userIds
          .map((id) => (id === me.id ? t('Tú') : (memberById(id)?.displayName ?? t('Alguien'))))
          .join(', ');
        return (
          <button
            key={reaction.emoji}
            type="button"
            title={t('{{names}} reaccionó con {{emoji}}', { names, emoji: reaction.emoji })}
            aria-pressed={mine}
            onClick={() => toggleReaction(reaction.emoji)}
            className={cn(
              'animate-pop inline-flex h-6 items-center gap-1 rounded-full border px-2 text-xs tabular-nums transition active:scale-90',
              mine
                ? 'border-accent/50 bg-accent-soft text-accent'
                : 'border-border bg-surface hover:border-border-strong',
            )}
          >
            <span className="text-sm leading-none">{reaction.emoji}</span>
            {reaction.count}
          </button>
        );
      })}
      <EmojiPicker
        size="sm"
        label={t('Añadir reacción')}
        placement="bottom"
        onPick={(emoji) => toggleReaction(emoji)}
      />
    </div>
  );
}

/** "3 respuestas · hace 5 min" under a message that started a thread. */
export function ThreadLink({ message }: { message: Message }) {
  const { openThread } = useTeam();
  const thread = message.thread;
  const { t } = useTranslation();
  if (!thread || thread.replyCount === 0) return null;
  return (
    <button
      type="button"
      onClick={() => openThread(message.id)}
      className="group/thread mt-1.5 flex max-w-md items-center gap-2 rounded-lg border border-transparent px-1.5 py-1 text-left hover:border-border hover:bg-surface"
    >
      <AvatarStack people={thread.participants} max={3} size="xs" />
      <span className="text-[13px] font-semibold text-accent group-hover/thread:underline">
        {thread.replyCount === 1
          ? t('1 respuesta')
          : t('{{count}} respuestas', { count: thread.replyCount })}
      </span>
      {thread.lastReplyAt && (
        <span className="text-xs text-subtle">
          {t('· última {{time}}', { time: formatRelative(thread.lastReplyAt) })}
        </span>
      )}
    </button>
  );
}

export function PinnedLabel({ message }: { message: Message }) {
  const { t } = useTranslation();
  if (!message.pinnedAt) return null;
  return (
    <p className="mb-0.5 flex items-center gap-1 text-[11px] font-medium text-amber-700 dark:text-amber-400">
      <Pin className="size-3" />{' '}
      {message.pinnedBy
        ? t('Fijado por {{name}}', { name: message.pinnedBy.displayName })
        : t('Fijado')}
    </p>
  );
}

export function LinkPreviews({ previews }: { previews: LinkPreview[] }) {
  if (previews.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-2">
      {previews.map((preview) => (
        <a
          key={preview.url}
          href={preview.url}
          target="_blank"
          rel="noreferrer noopener"
          onClick={(event) => {
            if (!isDesktop) return;
            event.preventDefault();
            void openExternal(preview.url);
          }}
          className="flex max-w-lg overflow-hidden rounded-xl border border-l-4 border-border border-l-accent bg-surface hover:border-border-strong"
        >
          <span className="min-w-0 flex-1 p-3">
            {preview.siteName && (
              <span className="block truncate text-[11px] font-medium tracking-wide text-subtle uppercase">
                {preview.siteName}
              </span>
            )}
            <span className="line-clamp-2 text-sm font-semibold text-accent">{preview.title}</span>
            {preview.description && (
              <span className="mt-0.5 line-clamp-2 text-xs text-muted">{preview.description}</span>
            )}
          </span>
          {preview.imageUrl && (
            <img
              src={preview.imageUrl}
              alt=""
              loading="lazy"
              referrerPolicy="no-referrer"
              onError={(e) => (e.currentTarget.style.display = 'none')}
              className="w-28 shrink-0 object-cover sm:w-36"
            />
          )}
        </a>
      ))}
    </div>
  );
}
